import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../context/authContext";
import AccountSettings from "../components/accountSettings";
import LocationPromptModal from "../components/locationReqModel";
import ShareModal from "../components/shareModal";
import FavoriteListCard from "../components/favoritelistcard";
import {
  fetchUserFavorites,
  deleteFavoriteList,
  renameFavoriteList,
  shareFavoriteList,
  fetchUserDetails,
  updateUserDetails,
} from "../api/getApi";
import Text from "../components/text";
import { Helmet } from "react-helmet-async";

const Profile = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("lists");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [shareModalOpen, setShareModalOpen] = useState(false);
  const [shareLink, setShareLink] = useState("");
  const navigate = useNavigate();
  const [editingIndex, setEditingIndex] = useState(null);
  const [newName, setNewName] = useState("");
  // State for user details fetched from database
  const [userDetails, setUserDetails] = useState({
    name: "",
    email: "",
    mobile: "",
    place: "",
    district: "",
    eamcetRank: "",
  });
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null);
  const [originalDetails, setOriginalDetails] = useState(null);

  const fetchUserDetailsHandler = useCallback(async () => {
    // No longer need to check for user.mobile, just authentication status
    if (!isAuthenticated) {
      setUserDetails({
        name: "",
        mobile: "",
        email: "",
        state: "",
        district: "",
        eamcetRank: "",
      });
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      // The function no longer takes any arguments
      const data = await fetchUserDetails();
      setUserDetails(data);
      setOriginalDetails(data);
    } catch (error) {
      console.error("Error fetching user details:", error);
      setError(error.message || "Failed to fetch user details");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]); // Dependency is now just on isAuthenticated

  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      const favoritesData = await fetchUserFavorites();
      setFavorites(favoritesData);
    } catch (error) {
      console.error("Error loading favorites:", error);
      setError(error.message || "Failed to load your saved lists");
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchUserDetailsHandler();
      loadFavorites();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, fetchUserDetailsHandler, loadFavorites]);

  // Add a function to check if user details are complete
  const isUserDetailsComplete = useCallback(() => {
    return !!(
      userDetails.eamcetRank &&
      userDetails.state &&
      userDetails.district
    );
  }, [userDetails]);

  const handleEdit = (favorite, index) => {
    // Check if user details are complete
    if (!isUserDetailsComplete()) {
      setShowLocationPrompt(true);
      setPendingEdit({ favorite, index });
      return;
    }

    // Check for the new 2025 list type
    if (favorite.formData && favorite.formData.note === "List created without rank criteria") {
      navigate(`/update-2025-ap-list`, {
        state: {
          favorite, // Pass the entire favorite object
          index,    // Pass the index
          editMode: true,
        },
      });
    } else {
      // Existing logic for other list types
      navigate(`/update`, {
        state: {
          favorite, // Pass the entire favorite object
          favoriteId: index,    // Pass the index with the correct key 'favoriteId'
          editMode: true,
        },
      });
    }
  };

  const handleDelete = async (index) => {
    if (!window.confirm("Are you sure you want to delete this list?")) return;
    try {
      await deleteFavoriteList(index); // Use index for deletion
      loadFavorites();
    } catch (error) {
      console.error("Error:", error);
      alert(error.message || "Failed to delete the list");
    }
  };

  const handleRenameStart = (index, currentName) => {
    setEditingIndex(index);
    setNewName(currentName || `List ${index + 1}`);
  };

  const handleRenameCancel = () => {
    setEditingIndex(null);
    setNewName("");
  };

  const handleRenameSubmit = async (index) => {
    if (!newName.trim()) {
      alert("List name cannot be empty");
      return;
    }
    try {
      await renameFavoriteList(index, newName.trim());
      setFavorites((prev) =>
        prev.map((fav, i) =>
          i === index ? { ...fav, name: newName.trim() } : fav
        )
      );
      setEditingIndex(null);
      setNewName("");
    } catch (error) {
      console.error("Error:", error);
      alert(error.message || "Failed to rename the list");
    }
  };

  const handleShare = async (index) => {
    try {
      const response = await shareFavoriteList(index);
      const shareableLink = `${window.location.origin}/shared/${response}`;
      setShareLink(shareableLink);
      setShareModalOpen(true);
    } catch (error) {
      console.error("Error:", error);
      alert(error.message || "Failed to create sharing link");
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    // Basic validation for mobile number

    try {
      await updateUserDetails(userDetails);
      alert("Profile updated successfully!");
      setIsEditingProfile(false);
      setOriginalDetails(userDetails); // Update original data after save
      if (showLocationPrompt && pendingEdit) {
        const { favorite, index } = pendingEdit;
        navigate(`/update`, {
          state: {
            selectedColleges: favorite.colleges,
            formData: favorite.formData,
            favoriteId: index,
            listName: favorite.name,
            editMode: true,
          },
        });
        setShowLocationPrompt(false);
        setPendingEdit(null);
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      alert(error.message || "Failed to update profile");
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setUserDetails((prev) => ({ ...prev, [name]: value }));
  };

  const handleCancelEdit = () => {
    setIsEditingProfile(false);
    if (originalDetails) {
      setUserDetails(originalDetails); // Revert to original data
    }
    if (showLocationPrompt) {
      setShowLocationPrompt(false);
      setPendingEdit(null);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <div className="container mx-auto px-4 py-12 flex-grow flex items-center justify-center">
          <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center transform transition-all hover:shadow-xl">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-16 w-16 text-blue-500 mx-auto mb-6 animate-pulse"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
              />
            </svg>
            <h2 className="text-3xl font-bold text-gray-800 mb-4">
              Please Log In
            </h2>
            <p className="text-gray-600 mb-6 leading-relaxed">
              {Text.Profile.Description}
            </p>
            <div className="space-y-3">
              <button
                onClick={() => navigate("/login")}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-700 text-white font-semibold py-3 px-4 rounded-lg hover:from-blue-600 hover:to-blue-800 transition-all duration-300 shadow-md"
              >
                Log In
              </button>
              <button
                onClick={() => navigate("/signup")}
                className="w-full bg-white border border-blue-500 text-blue-700 font-semibold py-3 px-4 rounded-lg hover:bg-blue-50 transition-all duration-300"
              >
                Create Account
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow-lg p-8 w-full max-w-md text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500 mx-auto mb-6"></div>
          <p className="text-gray-700 text-lg font-medium">
            {Text.Profile.Loading}
          </p>
          <p className="text-gray-500 mt-2">{Text.Profile.PleaseWait}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-4 py-6 md:py-10">
        {/* Profile Header */}
        <Helmet>
          <title>{Text.Profile.Title} | CollegeRank</title>
          <meta name="description" content={Text.Profile.Description} />
        </Helmet>
        <div className="bg-white rounded-xl shadow-md overflow-hidden mb-6">
          <div className="bg-gradient-to-r from-blue-600 to-blue-800 px-6 py-4 text-white">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
              <div className="mb-4 md:mb-0">
                <h1 className="text-2xl font-bold flex items-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-8 w-8 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  My Profile
                </h1>
                <div className="mt-1 text-blue-100">
                  {userDetails.mobile || "User"}{" "}
                  {/* Changed from email to mobile */}
                </div>
              </div>

              <div className="flex space-x-2">
                <button
                  onClick={() => navigate("/")}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M10 19l-7-7m0 0l7-7m-7 7h18"
                    />
                  </svg>
                  Home
                </button>
                <button
                  onClick={logout}
                  className="px-4 py-2 bg-blue-700 hover:bg-blue-800 text-white rounded-lg flex items-center justify-center transition-colors duration-200"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-1"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            </div>
          </div>

          {/* Profile Tabs */}
          <div className="px-6 py-3 border-b border-gray-200 flex">
            <button
              onClick={() => setActiveTab("lists")}
              className={`mr-6 py-2 border-b-2 font-medium text-sm ${
                activeTab === "lists"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Saved Lists
            </button>

            <button
              onClick={() => setActiveTab("account")}
              className={`py-2 border-b-2 font-medium text-sm ${
                activeTab === "account"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-500 hover:text-gray-700"
              }`}
            >
              Account Settings
            </button>
          </div>
        </div>

        {/* Content based on active tab */}
        {activeTab === "lists" && (
          <>
            {error && (
              <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 rounded-md">
                <p className="font-medium">Error</p>
                <p>{error}</p>
              </div>
            )}

            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800 flex items-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6 mr-2 text-blue-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                {Text.Profile.YourLists}
              </h2>
              <Link
                to="/filter"
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M12 6v6m6 0H6"
                  />
                </svg>
                Create New List
              </Link>
            </div>

            {favorites.length === 0 ? (
              <div className="bg-white rounded-xl shadow-md overflow-hidden text-center py-16 px-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-16 w-16 mx-auto text-gray-400 mb-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
                <h3 className="text-xl font-bold text-gray-700 mb-2">
                  {Text.Profile.NoLists}
                </h3>
                <p className="text-gray-500 max-w-md mx-auto mb-6">
                  {Text.Profile.CreateNewList}
                </p>
                <Link
                  to="/filter"
                  className="inline-flex items-center px-5 py-3 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-5 w-5 mr-2"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M12 6v6m6 0H6"
                    />
                  </svg>
                  {Text.Profile.CreateListLink}
                </Link>
              </div>
            ) : (
              <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                {favorites.map((favorite, index) => (
                  <FavoriteListCard
                    key={index}
                    favorite={favorite}
                    index={index}
                    editingIndex={editingIndex}
                    newName={newName}
                    setNewName={setNewName}
                    handleRenameSubmit={handleRenameSubmit}
                    handleRenameCancel={handleRenameCancel}
                    handleEdit={handleEdit}
                    handleShare={handleShare}
                    handleRenameStart={handleRenameStart}
                    handleDelete={handleDelete}
                  />
                ))}
              </div>
            )}
          </>
        )}

        {activeTab === "account" && (
          <AccountSettings
            activeTab={activeTab}
            userDetails={userDetails}
            setUserDetails={setUserDetails}
            isEditingProfile={isEditingProfile}
            setIsEditingProfile={setIsEditingProfile}
            handleProfileUpdate={handleProfileUpdate}
            handleCancelEdit={handleCancelEdit}
            showLocationPrompt={showLocationPrompt}
            logout={logout}
          />
        )}

        {/* Location Prompt Modal */}
        {showLocationPrompt && (
          <LocationPromptModal
            showLocationPrompt={showLocationPrompt}
            setShowLocationPrompt={setShowLocationPrompt}
            setPendingEdit={setPendingEdit}
            userDetails={userDetails}
            setUserDetails={setUserDetails}
            handleInputChange={handleInputChange}
            handleProfileUpdate={handleProfileUpdate}
            Text={Text}
          />
        )}
      </div>
      <ShareModal
        open={shareModalOpen}
        onClose={() => setShareModalOpen(false)}
        link={shareLink}
      />
    </div>
  );
};

export default Profile;
